import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Loader2, Gauge, Search, CheckCircle2, AlertTriangle, XCircle, Globe } from "lucide-react";
import { Seo } from "../components/Seo";
import { FadeUp } from "../components/Reveal";

import { track } from "../lib/track";
const EASE = [0.16, 1, 0.3, 1];


const STATUS_ICON = {
    pass: { icon: CheckCircle2, cls: "text-emerald-400" },
    warn: { icon: AlertTriangle, cls: "text-amber-400" },
    fail: { icon: XCircle, cls: "text-rose-400" },
};

const GROUPS = [
    { id: "speed", label: "Speed & delivery", icon: Gauge },
    { id: "seo", label: "SEO & content", icon: Search },
];

export default function SiteAudit() {
    const [url, setUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const run = async (e) => {
        e.preventDefault();
        if (!url.trim()) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const r = await fetch("/api/site-audit", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ url: url.trim() }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(data.detail || "");
            setResult(data);
            track("website_audit_complete", { score: data.score });
        } catch (err) {
            setError(err.message || "We couldn't test that address. Check the URL and try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="site-audit-page">
            <Seo
                title="Test Your Website — Free Speed & SEO Check | GrowVise"
                description="Free instant website check for NZ businesses: speed, delivery and essential SEO signals — then practical advice on what to fix first."
                path="/test-your-website"
            />

            <section className="bg-navy-950 pt-32 sm:pt-40 pb-20 min-h-screen" data-testid="audit-shell">
                <div className="max-w-3xl mx-auto px-5 sm:px-8">
                    <FadeUp>
                        <p className="eyebrow mb-6">Free website check · 20 seconds</p>
                        <h1 className="font-serif font-bold tracking-tight leading-[1.08] text-4xl sm:text-5xl text-slate-50" data-testid="audit-title">
                            Test your <span className="text-gold-500">website.</span>
                        </h1>
                        <p className="mt-6 text-lg text-slate-300 font-light leading-relaxed max-w-xl">
                            A quick automated snapshot of your site's speed and essential SEO signals — the same basics we check first for any product business.
                        </p>
                    </FadeUp>

                    <FadeUp delay={0.1}>
                        <form onSubmit={run} className="card-surface p-6 sm:p-8 mt-10" data-testid="audit-form">
                            <label htmlFor="audit-url" className="block text-xs font-mono tracking-widest uppercase text-slate-500 mb-2">
                                Your website address
                            </label>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <div className="relative flex-1">
                                    <Globe size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                                    <input
                                        id="audit-url"
                                        className="input-field pl-11"
                                        placeholder="yourbusiness.co.nz"
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        data-testid="audit-url-input"
                                    />
                                </div>
                                <button type="submit" disabled={loading || !url.trim()} className="btn-gold disabled:opacity-50 shrink-0" data-testid="audit-run-btn">
                                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                                    {loading ? "Testing…" : "Run the test"}
                                </button>
                            </div>
                            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                                We fetch your homepage once and check response time, page weight, compression, caching and core SEO tags. Nothing is stored against your business.
                            </p>
                            {error && (
                                <p className="mt-4 text-sm text-rose-400" data-testid="audit-error">{error}</p>
                            )}
                        </form>
                    </FadeUp>

                    <AnimatePresence>
                        {result && (
                            <motion.div
                                key="results"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, ease: EASE }}
                                className="mt-10"
                                data-testid="audit-results"
                            >
                                <div className="card-surface p-8 sm:p-10">
                                    <div className="flex flex-col sm:flex-row items-center gap-8 pb-8 border-b border-white/10">
                                        <div className="relative w-36 h-36 shrink-0" data-testid="audit-score-dial">
                                            <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
                                                <circle cx="80" cy="80" r="64" fill="none" stroke="#142747" strokeWidth="10" />
                                                <motion.circle
                                                    cx="80" cy="80" r="64" fill="none" stroke="#D4AF37" strokeWidth="10" strokeLinecap="round"
                                                    strokeDasharray={2 * Math.PI * 64}
                                                    initial={{ strokeDashoffset: 2 * Math.PI * 64 }}
                                                    animate={{ strokeDashoffset: 2 * Math.PI * 64 * (1 - result.score / 100) }}
                                                    transition={{ duration: 1.4, ease: EASE }}
                                                />
                                            </svg>
                                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                <span className="font-serif font-bold text-3xl text-slate-50" data-testid="audit-score">{result.score}</span>
                                                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mt-1">/ 100</span>
                                            </div>
                                        </div>
                                        <div className="text-center sm:text-left">
                                            <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-500">Results for</p>
                                            <p className="font-serif text-xl text-slate-50 mt-1 break-all" data-testid="audit-tested-url">{result.final_url}</p>
                                            <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                                                Loaded in <span className="text-gold-400 font-mono">{result.elapsed_ms}ms</span>. This is a quick automated snapshot — a full commercial review covers far more.
                                            </p>
                                        </div>
                                    </div>

                                    {GROUPS.map((g) => (
                                        <div key={g.id} className="mt-8" data-testid={`audit-group-${g.id}`}>
                                            <p className="flex items-center gap-2.5 font-mono text-xs tracking-[0.25em] uppercase text-gold-400 mb-4">
                                                <g.icon size={15} /> {g.label}
                                            </p>
                                            <div className="space-y-2.5">
                                                {result.checks.filter((c) => c.group === g.id).map((c) => {
                                                    const S = STATUS_ICON[c.status];
                                                    return (
                                                        <div key={c.id} className="flex items-start gap-3 bg-navy-900/60 border border-white/5 rounded-xl px-4 py-3" data-testid={`audit-check-${c.id}`}>
                                                            <S.icon size={17} className={`${S.cls} mt-0.5 shrink-0`} />
                                                            <div className="min-w-0">
                                                                <p className="text-sm text-slate-200 font-medium">{c.label}</p>
                                                                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed break-words">{c.detail}</p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="card-surface mt-6 p-8 sm:p-10 text-center border-gold-500/30" data-testid="audit-cta">
                                    <h2 className="font-serif text-2xl sm:text-3xl text-slate-50">
                                        Get in touch with GrowVise today to see how we can help you.
                                    </h2>
                                    <p className="mt-4 text-slate-400 leading-relaxed max-w-xl mx-auto">
                                        A faster, sharper website is only part of the picture. Book a free 30-minute Growth Conversation about what's really holding the business back.
                                    </p>
                                    <div className="mt-8 flex flex-wrap justify-center gap-4">
                                        <Link to="/book-a-conversation" className="btn-gold" data-testid="audit-book-cta">
                                            Book a Growth Conversation <ArrowRight size={16} />
                                        </Link>
                                        <Link to="/business-health-check" className="btn-outline" data-testid="audit-healthcheck-cta">
                                            Free Business Health Check
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>
        </div>
    );
}
